import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";
import { Card, CardBody, Row, Col } from "reactstrap";

import { H3, H4, MUIIcons, P } from "../../AbstractElements";
import { ILT_VILT_COURSE_FILTER_STATUS } from "../../Api/constants";
import Btn from "../../CommonElements/Button";
import CommonModal from "../../CommonElements/Modal";
import SearchBar from "../../CommonElements/SearchBar";
import SearchDropDownPaginator from "../../CommonElements/SearchDropDownPaginator";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import {
  createcourseEquivalence,
  getCourseEquivalence,
  getCourseList,
  setCourseListToNull,
} from "../../ReduxToolkit/Reducers/CourseSlice";
import { getDomainList } from "../../ReduxToolkit/Reducers/MasterSlice";
import {
  CreateNewMapping,
  EditEquivalenceMapping,
  NoEquivalenceMappingsFound,
  CreateYourFirstEquivalenceMapping,
  MapsToMainCourses,
  ConfirmDeleteEquivalenceMapping,
  PleaseSelectEquivalentCourse,
  PleaseSelectAtLeastOneMainCourse,
  SelectEquivalentCourse,
  SelectMainCourses,
  SaveMapping,
  Edit,
  Delete,
  Cancel,
  Close,
  Courses,
} from "../../utils/Constant";
import { customStyles, showToast } from "../../utils/helper/helper";
import useIsMobile from "../../utils/helper/responsive";

import "./style.scss";

interface EquivalenceMapping {
  id: number;
  equivalentCourse: Equivalentcourse;
  mainCourses: Equivalentcourse[];
}

const CourseEquivalence: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  // State
  const { courseList, courseEquivalenceList } = useAppSelector(
    state => state.course
  );
  const [equivalenceMappings, setEquivalenceMappings] = useState<
    EquivalenceMapping[]
  >([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [showCoursesModal, setShowCoursesModal] = useState(false);
  const [clearData, setClearData] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [coursesModalMapping, setCoursesModalMapping] =
    useState<EquivalenceMapping | null>(null);
  const [currentEditingId, setCurrentEditingId] = useState<number | null>(null);
  const [selectedEquivalentCourse, setSelectedEquivalentCourse] =
    useState<any>(null);
  const [selectedMainCourses, setSelectedMainCourses] = useState<any[]>([]);
  const [, setEquivalentCourseDropdownActive] = useState(false);
  const [, setMainCoursesDropdownActive] = useState(false);

  useEffect(() => {
    dispatch(getDomainList());
    dispatch(setCourseListToNull());
    fetchCourses();
  }, []);

  useEffect(() => {
    if (courseEquivalenceList?.course_equivalence) {
      const apiMappings: EquivalenceMapping[] =
        courseEquivalenceList.course_equivalence.map(item => ({
          id: item.equivalent_course.id,
          equivalentCourse: item.equivalent_course,
          mainCourses: item.main_courses,
        }));
      setEquivalenceMappings(apiMappings);
    } else {
      setEquivalenceMappings([]);
    }
  }, [courseEquivalenceList]);

  const fetchCourses = () => {
    dispatch(getCourseEquivalence());
    dispatch(
      getCourseList({
        status: ["published"],
        course_type: ILT_VILT_COURSE_FILTER_STATUS,
        domain: [],
        page: "course_management",
      })
    );
  };

  const handleSearch = (query: string) => {
    setClearData(false);
    setSearchQuery(query);
  };

  // Modal open/close helpers
  const openCreateModal = () => {
    setCurrentEditingId(null);
    setSelectedEquivalentCourse(null);
    setSelectedMainCourses([]);
    setShowMappingModal(true);
  };
  const openEditModal = (id: number) => {
    const mapping = equivalenceMappings.find(m => m.id === id);
    if (!mapping) return;
    setCurrentEditingId(id);
    setSelectedEquivalentCourse(mapping.equivalentCourse);
    setSelectedMainCourses([...mapping.mainCourses]);
    setShowMappingModal(true);
  };
  const closeMappingModal = () => {
    setShowMappingModal(false);
    setSelectedEquivalentCourse(null);
    setSelectedMainCourses([]);
    setEquivalentCourseDropdownActive(false);
    setMainCoursesDropdownActive(false);
  };

  // Courses modal
  const showAllCourses = (mapping: EquivalenceMapping) => {
    setCoursesModalMapping(mapping);
    setShowCoursesModal(true);
  };

  // Dropdown logic
  const availableEquivalentCourses = courseList?.course_list.filter(course => {
    // Exclude if already used as equivalentCourse in another mapping (unless editing that mapping)
    const isAlreadyMapped = equivalenceMappings.some(
      m => m.equivalentCourse.id === course.id && m.id !== currentEditingId
    );
    // Exclude if selected as a main course in the current modal
    const isSelectedAsMain = selectedMainCourses.some(
      selected => selected.id === course.id
    );
    return !isAlreadyMapped && !isSelectedAsMain;
  });
  const availableMainCourses = courseList?.course_list.filter(
    course =>
      (!selectedEquivalentCourse ||
        course.id !== selectedEquivalentCourse.id) &&
      !selectedMainCourses.some(selected => selected.id === course.id)
  );

  // Mapping CRUD
  const handleDeleteMapping = (id: number) => {
    if (window.confirm(ConfirmDeleteEquivalenceMapping)) {
      setEquivalenceMappings(prev => prev.filter(m => m.id !== id));
    }
  };
  const handleSaveMapping = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEquivalentCourse) {
      showToast(PleaseSelectEquivalentCourse, "error");
      return;
    }
    if (selectedMainCourses.length === 0) {
      showToast(PleaseSelectAtLeastOneMainCourse, "error");
      return;
    }
    if (currentEditingId) {
      //Edit Flow
    } else {
      //Create Flow
      const payload = {
        equivalent_course_id: selectedEquivalentCourse.id,
        course_ids: selectedMainCourses.map(course => course.id),
      };
      dispatch(
        createcourseEquivalence({
          courseEquivalence: payload,
        })
      ).then(res => {
        if (res?.payload) {
          closeMappingModal();
          fetchCourses();
        }
      });
    }
  };

  const handleSaveMappingClick = () => {
    // Call the save logic without needing an event
    handleSaveMapping(new Event("submit") as any);
  };

  // Filtered mappings based on searchQuery
  const displayedMappings = searchQuery
    ? equivalenceMappings.filter(mapping =>
        mapping.equivalentCourse.course_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : equivalenceMappings;

  // UI
  return (
    <div className="page-body">
      <div className="container-fluid"></div>
      <Card className="p-1">
        <CardBody>
          <Row
            className={`d-flex flex-column flex-md-row justify-content-between ${!isMobile ? "align-items-center" : ""}`}
          >
            <Col
              lg="6"
              md="12"
              sm="12"
              xs="12"
              className="mb-1 mb-sm-0 d-flex flex-column gap-2"
            ></Col>
            <Col
              lg="6"
              md="12"
              sm="12"
              xs="12"
              className="d-flex flex-wrap justify-content-end align-items-center gap-3 ms-auto"
            >
              <div className="w-100 d-block d-sm-none mb-2">
                <SearchBar
                  showSearch
                  onSearch={handleSearch}
                  clearOnProp={clearData}
                />
              </div>
              <div className="d-none d-sm-block">
                <SearchBar
                  showSearch
                  onSearch={handleSearch}
                  clearOnProp={clearData}
                />
              </div>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Btn
                  icon={
                    <MUIIcons size={16} iconName="AddCircleOutlineOutlined" />
                  }
                  color="primary"
                  onClick={openCreateModal}
                >
                  {CreateNewMapping}
                </Btn>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>
      <div className="container">
        {displayedMappings.length === 0 ? (
          <div className="empty-state">
            <H3>{NoEquivalenceMappingsFound}</H3>
            <P>{CreateYourFirstEquivalenceMapping}</P>
          </div>
        ) : (
          <div className="equivalence-grid">
            {displayedMappings.map(mapping => {
              // Show preview in 2 rows, e.g., 5 chips per row
              const chipsPerRow = 5;
              const previewCourses = mapping.mainCourses.slice(
                0,
                chipsPerRow * 2
              );
              const remainingCount =
                mapping.mainCourses.length - previewCourses.length;
              const firstRow = previewCourses.slice(0, chipsPerRow);
              const secondRow = previewCourses.slice(
                chipsPerRow,
                chipsPerRow * 2
              );
              return (
                <div className="equivalence-card" key={mapping.id}>
                  <div className="equivalent-course">
                    {mapping.equivalentCourse.course_id} -
                    {mapping.equivalentCourse.course_name}
                  </div>
                  <div
                    className="main-courses-section"
                    onClick={() => showAllCourses(mapping)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="main-courses-header">
                      <div className="main-courses-label">
                        {MapsToMainCourses}
                      </div>
                      <div className="course-count">
                        {mapping.mainCourses.length} {Courses}
                      </div>
                    </div>
                    <div className="main-courses-preview">
                      {/* First row */}
                      {firstRow.map(course => (
                        <span className="main-course" key={course.id}>
                          {course.course_id}
                        </span>
                      ))}
                      {/* Second row */}
                      {secondRow.map(course => (
                        <span className="main-course" key={course.id}>
                          {course.course_id}
                        </span>
                      ))}
                      {/* +N more button as last item in second row */}
                      {remainingCount > 0 && (
                        <button
                          className="show-more-btn"
                          onClick={e => {
                            e.stopPropagation();
                            showAllCourses(mapping);
                          }}
                        >
                          +{remainingCount} more
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="card-actions">
                    <Btn
                      color="primary"
                      onClick={() => openEditModal(mapping.id)}
                    >
                      {Edit}
                    </Btn>
                    <Btn
                      color="primary"
                      onClick={() => handleDeleteMapping(mapping.id)}
                    >
                      {Delete}
                    </Btn>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Courses Detail Modal */}
        {showCoursesModal && coursesModalMapping && (
          <div className="courses-modal">
            <div className="courses-modal-content">
              <H3>
                <span>⭐</span>
                {`${coursesModalMapping.equivalentCourse.course_id} - ${coursesModalMapping.equivalentCourse.course_name}`}
              </H3>
              <H4 className="mt-2">
                {`${MapsToMainCourses} ${coursesModalMapping.mainCourses.length}`}
                {Courses}
              </H4>
              <div className="courses-grid">
                {coursesModalMapping.mainCourses.map(course => (
                  <div className="course-item" key={course.id}>
                    <div className="course-code">{course.course_name}</div>
                    <div className="course-name">{course.course_id}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Btn color="primary" onClick={() => setShowCoursesModal(false)}>
                  {Close}
                </Btn>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        <CommonModal
          isOpen={showMappingModal}
          toggle={closeMappingModal}
          title={currentEditingId ? EditEquivalenceMapping : CreateNewMapping}
          showFooter={true}
          primaryBtnText={SaveMapping}
          secondaryBtnText={Cancel}
          onPrimaryBtnClick={handleSaveMappingClick}
          onSecondaryBtnClick={closeMappingModal}
          size="xl"
          modalBodyClassName=""
        >
          <form onSubmit={handleSaveMapping}>
            {/* Equivalent Course Selector */}
            <div className="course-selector">
              <H4>{SelectEquivalentCourse}</H4>
              <ReactSelect
                name="equivalentCourse"
                className="text-gray"
                value={
                  selectedEquivalentCourse
                    ? {
                        value: selectedEquivalentCourse.id,
                        label: `${selectedEquivalentCourse.course_id} - ${selectedEquivalentCourse.course_name}`,
                      }
                    : null
                }
                onChange={option => {
                  setSelectedEquivalentCourse(
                    option
                      ? courseList?.course_list.find(
                          course => course.id === option.value
                        )
                      : null
                  );
                }}
                options={availableEquivalentCourses.map(course => ({
                  value: course.id,
                  label: `${course.course_id} - ${course.course_name}`,
                }))}
                isSearchable
                isClearable={false}
                styles={customStyles}
                components={{
                  MenuList: props => (
                    <SearchDropDownPaginator {...props} maxHeight={250} />
                  ),
                }}
              />
            </div>
            {/* Main Courses Selector */}
            <div className="course-selector">
              <H4>{SelectMainCourses}</H4>
              <ReactSelect
                name="mainCourses"
                className="text-gray"
                value={selectedMainCourses.map(course => ({
                  value: course.id,
                  label: `${course.course_id} - ${course.course_name}`,
                }))}
                onChange={options => {
                  setSelectedMainCourses(
                    options
                      ? options.map(option =>
                          courseList?.course_list.find(
                            course => course.id === option.value
                          )
                        )
                      : []
                  );
                }}
                options={availableMainCourses.map(course => ({
                  value: course.id,
                  label: `${course.course_id} - ${course.course_name}`,
                }))}
                isMulti
                isSearchable
                isClearable={false}
                styles={customStyles}
                components={{
                  MenuList: props => (
                    <SearchDropDownPaginator {...props} maxHeight={250} />
                  ),
                }}
              />
            </div>
          </form>
        </CommonModal>
      </div>
    </div>
  );
};

export default CourseEquivalence;
